import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { FaRegClock, FaRegUser, FaRegCalendarAlt } from 'react-icons/fa';
import { BLOGS_PAGE } from '../config/content';
import { formatMarketDate } from '../config/region';
import styles from './Blogs.module.css';
import SEO from '../components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

function AnimatedSection({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
}

interface BlogMeta {
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  author: string;
  image: string;
}

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogMeta[]>([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/data/blogs/manifest.json')
      .then((res) => res.json())
      .then((data: BlogMeta[]) => {
        setBlogs(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered =
    activeCategory === 'All'
      ? blogs
      : blogs.filter((p) => p.category === activeCategory);

  const formatDate = (dateStr: string) => formatMarketDate(new Date(dateStr));

  // First 3 blogs are featured
  const featured = blogs.slice(0, 3);
  // All filtered blogs shown in grid (including featured)
  const gridPosts = filtered;

  return (
    <div className={styles.page}>
      <SEO path="/blogs" />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* Hero + Featured Articles */}
      <section className={styles.heroFeaturedSection}>
        <div className={styles.container}>
          <AnimatedSection>
            <motion.div className={styles.heroFeaturedGrid} variants={fadeInUp}>
              {/* Left — Hero text */}
              <div className={styles.heroLeft}>
                <div className={styles.heroBadge}>
                  <div className={styles.badgeDot} />
                  <span>Knowledge Hub</span>
                </div>
                <h1 className={styles.heroTitle}>Blog &amp; <em>Insights</em></h1>
                <p className={styles.heroSubtitle}>{BLOGS_PAGE.hero.subtitle}</p>
              </div>

              {/* Right — 4 Featured articles */}
              {!loading && featured.length >= 3 && (
                <div className={styles.featuredSide}>
                  {featured.slice(0, 3).map((post) => (
                    <Link key={post.slug} to={`/blogs/${post.slug}`} className={styles.featuredCardSide}>
                      <div className={styles.featuredImageSide}>
                        <img src={`/data/blogs/${post.slug}/${post.image}`} alt={post.title} />
                      </div>
                      <div className={styles.featuredBodySide}>
                        <span className={styles.blogCategory}>{post.category}</span>
                        <h3 className={styles.blogTitle}>{post.title}</h3>
                        <div className={styles.blogMeta}>
                          <span className={styles.blogMetaItem}><FaRegCalendarAlt /> {formatDate(post.date)}</span>
                          <span className={styles.blogMetaItem}><FaRegClock /> {post.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      <div className={styles.sectionDivider} />

      {/* Filter Tabs */}
      <section className={styles.filterSection}>
        <div className={styles.container}>
          <div className={styles.filterTabs}>
            {BLOGS_PAGE.categories.map((cat) => (
              <button
                key={cat}
                className={`${styles.filterTab} ${activeCategory === cat ? styles.filterTabActive : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className={styles.section}>
        <AnimatedSection className={styles.container}>
          {loading ? (
            <p style={{ textAlign: 'center', color: 'rgba(245,242,235,0.4)', padding: '60px 0' }}>{BLOGS_PAGE.loading}</p>
          ) : gridPosts.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'rgba(245,242,235,0.4)', padding: '60px 0' }}>{BLOGS_PAGE.noPosts}</p>
          ) : (
            <>
              <motion.span className={styles.allArticlesLabel} variants={fadeInUp}>All Articles</motion.span>
              <div className={styles.blogGrid}>
                {gridPosts.map((post) => (
                  <motion.div key={post.slug} variants={fadeInUp}>
                    <Link to={`/blogs/${post.slug}`} className={styles.blogCard}>
                      <div className={styles.blogImage}>
                        <img src={`/data/blogs/${post.slug}/${post.image}`} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div className={styles.blogBody}>
                        <span className={styles.blogCategory}>{post.category}</span>
                        <h3 className={styles.blogTitle}>{post.title}</h3>
                        <p className={styles.blogExcerpt}>{post.excerpt}</p>
                        <div className={styles.blogMeta}>
                          <span className={styles.blogMetaItem}>
                            <FaRegCalendarAlt /> {formatDate(post.date)}
                          </span>
                          <span className={styles.blogMetaItem}>
                            <FaRegClock /> {post.readTime}
                          </span>
                          <span className={styles.blogMetaItem}>
                            <FaRegUser /> {post.author}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </AnimatedSection>
      </section>
    </div>
  );
};

export default Blogs;
